import store from './store';
import Vue from 'vue';
import dayjs from 'dayjs';

const testPermissions = (callback, requestedPermissions) => {
  if (requestedPermissions == null) {
    return true;
  }

  if (!Array.isArray(requestedPermissions)) {
    requestedPermissions = [requestedPermissions]
  }

  if (store?.state?.auth?.permissions) {
    if (typeof callback === 'function') {
      return callback(requestedPermissions);
    }
  }
  return false;
}

const auth = {
  init (params) {
    let fetchUser = params && params.fetchUser || true;

    if (params?.router) {
      this.setupRouter(params.router, params.routerOptions || null);
    }

    Vue.http.interceptors.push((request, next) => {
      next((response) => {
        if (response.status === 401) {
          this.resetAuth();
        }
      });
    });

    if (store.getters.user?.token?.expiresAt) {
      const sessionExpiresAt = dayjs(store.getters.user.token.expiresAt);
      const now = dayjs();
      if (sessionExpiresAt.isBefore(now)) {
        console.debug('Authentication user token has expired, resetting auth...');
        this.resetAuth();
      }
    }

    if (store.getters.isAuthenticated && fetchUser) {
      this.fetchUser();
    }
  },

  login (credentials) {
    let request = Vue.http.post('auth/login', credentials);

    request.then(response => this.fetchUser());

    return request;
  },

  logout () {
    let request = Vue.http.post('auth/logout');

    request.then(response => {
      this.resetAuth();
    });

    return request;
  },

  setAuth (auth) {
    return store.dispatch('setAuth', auth);
  },

  fetchUser () {
    let auth = {
      isAuthenticated: false,
      user: {},
      permissions: {}
    };
    let requestData = Vue.http.get('auth/myself');
    requestData.then(response => auth.user = response.data);

    let requestPermissions = Vue.http.get('auth/permissions');
    requestPermissions.then(response => auth.permissions = response.data);

    let allRequests = Promise.all([requestData, requestPermissions]);
    allRequests.then(() => {
      this.prepareAuthAnyPermissions(auth.permissions);
      auth.isAuthenticated = !!(auth.user && auth.user.email);
      this.setAuth(auth);
    });

    return allRequests;
  },

  prepareAuthAnyPermissions (permissions) {
    if (permissions == null) {
      permissions = {};
    } else {
      let anyPermissions = {};

      const addAll = function (permissions) {
        for (const permission of permissions) {
          if (permission) {
            anyPermissions[permission] = true;
          }
        }
      }

      addAll(permissions.anyPermissions);

      permissions.anyPermissions = anyPermissions;
    }
  },

  resetAuth () {
    this.setAuth({
      isAuthenticated: false,
      user: {},
      permissions: {}
    });
  },

  hasAnyPermission (requestedPermissions) {
    return testPermissions(
      requestedPermissions => {
        if (store?.state?.auth?.permissions.anyPermissions) {
          for (const permission of requestedPermissions) {
            if (store?.state?.auth?.permissions.anyPermissions[permission] == null) {
              return false;
            }
          }
          return true;
        }
      },
      requestedPermissions
    )
  },

  hasGlobalPermission (requestedPermissions) {
    return testPermissions(
      requestedPermissions => {
        if (store?.state?.auth?.permissions.globalPermissions) {
          for(const permission of requestedPermissions) {
            if (store?.state?.auth?.permissions.globalPermissions.indexOf(permission) < 0) {
              return false;
            }
          }
          return true;
        }
      },
      requestedPermissions
    );
  },

  hasManagerUserPermission (managerUserId, requestedPermissions) {
    if (managerUserId != null && typeof managerUserId === 'object') {
      managerUserId = managerUserId['managerUserId'];
    }

    return this.hasGlobalPermission(requestedPermissions) || testPermissions(
      requestedPermissions => {
        if (store?.state?.auth?.permissions.managerUserPermissions) {
          let permissionsList = store?.state?.auth?.permissions.managerUserPermissions[managerUserId];
          if (permissionsList) {
            for (const permission of requestedPermissions) {
              if (permissionsList.indexOf(permission) < 0) {
                return false;
              }
            }
            return true;
          }
          return false;
        }
      },
      requestedPermissions
    );
  },

  setupRouter (router, options) {
    options = Object.assign({
      notAuthorizedRedirectRoute: null
    }, options)

    router.beforeEach((to, from, next) => {
      if (to.meta?.permission) {
        if (!auth.hasAnyPermission(to.meta.permission)) {
          if (options.notAuthorizedRedirectRoute) {
            if (typeof options.notAuthorizedRedirectRoute === 'string') {
              next({name: options.notAuthorizedRedirectRoute});
            } else {
              next(options.notAuthorizedRedirectRoute);
            }
          } else {
            next({
              path: '/404'
            })
          }
        } else {
          next()
        }
      } else {
        next()
      }
    })
  }
}

export default auth
